/**
 * Feed Screen — main feed with search, For You / Following tabs.
 * Tab design matches Home module (pill style).
 * Pull-to-refresh + infinite scroll + FAB + user search.
 * View tracking fires when post enters viewport.
 */

import { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { Search, Plus, Loader2 } from 'lucide-react'
import { useAuthStore } from '@turnifly/shared/stores'
import { useForYouFeed, useFollowingFeed, useFollowingSets, feedKeys } from '@turnifly/shared/hooks'
import { useToggleFollow } from '@turnifly/shared/hooks'
import { recordPostView } from '@turnifly/shared/api'
import { PostCard } from './post-card'
import { PostDetailSheet } from './post-detail-sheet'
import { PostComposer } from './post-composer'
import { RepostSheet } from './repost-sheet'
import { UserSearch } from './user-search'
import { Skeleton } from '@turnifly/shared/ui'
import { EmptyState } from '@turnifly/shared/ui'
import type { FeedItem } from '@turnifly/shared/types'

type FeedTab = 'for-you' | 'following'

const PULL_THRESHOLD = 80

export function FeedScreen(): React.ReactElement {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const userId = user?.id ?? ''
  const queryClient = useQueryClient()
  const scrollRef = useRef<HTMLDivElement>(null)
  const viewedRef = useRef<Set<string>>(new Set())

  const [searchParams, setSearchParams] = useSearchParams()
  const [tab, setTab] = useState<FeedTab>('for-you')
  const [composerOpen, setComposerOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [detailPost, setDetailPost] = useState<{ id: string; type: string } | null>(null)
  const [repostTarget, setRepostTarget] = useState<FeedItem | null>(null)

  // Deep link: open post detail from ?post=uuid
  const deepLinkPostId = searchParams.get('post')
  useEffect(() => {
    if (deepLinkPostId) {
      setDetailPost({ id: deepLinkPostId, type: 'user_post' })
      // Clear param so it doesn't reopen on re-render
      setSearchParams({}, { replace: true })
    }
  }, [deepLinkPostId, setSearchParams])

  // Pull-to-refresh + tab swipe state
  const [pullDistance, setPullDistance] = useState(0)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const touchStartY = useRef(0)
  const touchStartX = useRef(0)
  const isPulling = useRef(false)

  const FEED_TABS: FeedTab[] = ['for-you', 'following']
  const SWIPE_THRESHOLD = 60

  // Feed queries
  const forYou = useForYouFeed(tab === 'for-you' ? userId : undefined)
  const following = useFollowingFeed(tab === 'following' ? userId : undefined)
  const { data: followSets } = useFollowingSets(userId || undefined)
  const toggleFollow = useToggleFollow()

  const activeFeed = tab === 'for-you' ? forYou : following
  const allPosts = useMemo<FeedItem[]>(
    () => activeFeed.data?.pages.flat() ?? [],
    [activeFeed.data],
  )

  const isLoading = activeFeed.isLoading
  const hasMore = activeFeed.hasNextPage

  // ── Pull-to-refresh + tab swipe ──
  const handleTouchStart = useCallback((e: React.TouchEvent): void => {
    touchStartX.current = e.touches[0].clientX
    touchStartY.current = e.touches[0].clientY
    const el = scrollRef.current
    if (el && el.scrollTop <= 0 && !isRefreshing) {
      isPulling.current = true
    }
  }, [isRefreshing])

  const handleTouchMove = useCallback((e: React.TouchEvent): void => {
    if (!isPulling.current) return
    const dy = e.touches[0].clientY - touchStartY.current
    if (dy < 0) {
      isPulling.current = false
      setPullDistance(0)
      return
    }
    setPullDistance(Math.min(dy * 0.5, 130))
  }, [])

  const handleTouchEnd = useCallback((e: React.TouchEvent): void => {
    const dx = e.changedTouches[0].clientX - touchStartX.current
    const dy = e.changedTouches[0].clientY - touchStartY.current

    // Horizontal swipe → tab switch (only if not on media carousel)
    const target = e.target as HTMLElement
    if (Math.abs(dx) >= SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy) * 1.5 && !target.closest('[data-carousel]')) {
      const idx = FEED_TABS.indexOf(tab)
      const nextIdx = dx < 0 ? idx + 1 : idx - 1
      if (nextIdx >= 0 && nextIdx < FEED_TABS.length) {
        setTab(FEED_TABS[nextIdx])
      }
      isPulling.current = false
      setPullDistance(0)
      return
    }

    // Vertical pull-to-refresh
    if (!isPulling.current) return
    isPulling.current = false

    if (pullDistance >= PULL_THRESHOLD && !isRefreshing) {
      setIsRefreshing(true)
      setPullDistance(PULL_THRESHOLD * 0.6)

      const key = tab === 'for-you' ? feedKeys.forYou(userId) : feedKeys.following(userId)
      void queryClient.invalidateQueries({ queryKey: key }).then(() => {
        void queryClient.invalidateQueries({ queryKey: feedKeys.followingIds(userId) })
        setIsRefreshing(false)
        setPullDistance(0)
      })
    } else {
      setPullDistance(0)
    }
  }, [pullDistance, isRefreshing, tab, userId, queryClient])

  // ── Track views via IntersectionObserver ──
  useEffect(() => {
    if (!userId || allPosts.length === 0) return

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const postId = entry.target.getAttribute('data-post-id')
            if (postId && !viewedRef.current.has(postId)) {
              viewedRef.current.add(postId)
              void recordPostView(postId, userId)
            }
          }
        }
      },
      { threshold: 0.5 },
    )

    const container = scrollRef.current
    if (!container) return

    const articles = container.querySelectorAll('[data-post-id]')
    articles.forEach((el) => observer.observe(el))

    return () => observer.disconnect()
  }, [userId, allPosts])

  // ── Infinite scroll ──
  const handleScroll = useCallback((): void => {
    const el = scrollRef.current
    if (!el) return
    const { scrollTop, scrollHeight, clientHeight } = el
    if (scrollHeight - scrollTop - clientHeight < 400 && hasMore && !activeFeed.isFetchingNextPage) {
      void activeFeed.fetchNextPage()
    }
  }, [hasMore, activeFeed])

  const handleCommentPress = useCallback((postId: string): void => {
    const post = allPosts.find((p) => p.post_id === postId)
    setDetailPost(post ? { id: postId, type: post.post_type } : null)
  }, [allPosts])

  const handleRepostPress = useCallback((postId: string): void => {
    const post = allPosts.find((p) => p.post_id === postId)
    if (post) setRepostTarget(post)
  }, [allPosts])

  // Optimistic follow — tracks local follow actions until server confirms
  const [localFollows, setLocalFollows] = useState<Map<string, 'accepted' | 'pending'>>(new Map())

  const handleFollowPress = useCallback((targetId: string): void => {
    if (!userId) return
    setLocalFollows((prev) => new Map(prev).set(targetId, 'accepted'))
    toggleFollow.mutate({
      followerId: userId,
      followingId: targetId,
      isTargetPrivate: false,
      isCurrentlyFollowing: false,
    }, {
      onError: () => {
        setLocalFollows((prev) => {
          const next = new Map(prev)
          next.delete(targetId)
          return next
        })
      },
    })
  }, [userId, toggleFollow])

  // Merge server data with optimistic local state
  const serverAccepted = followSets?.accepted ?? new Set<string>()
  const serverPending = followSets?.pending ?? new Set<string>()

  const followingSet = useMemo(() => {
    const merged = new Set(serverAccepted)
    for (const [id, status] of localFollows) {
      if (status === 'accepted') merged.add(id)
    }
    return merged
  }, [serverAccepted, localFollows])

  const pendingSet = useMemo(() => {
    const merged = new Set(serverPending)
    for (const [id, status] of localFollows) {
      if (status === 'pending') merged.add(id)
    }
    return merged
  }, [serverPending, localFollows])

  return (
    <div
      ref={scrollRef}
      onScroll={handleScroll}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      className="h-full overflow-y-auto overscroll-y-contain"
    >
      {/* ── Fixed header ── */}
      <div className="sticky top-0 z-20 tg-header-safe-area">
        <div className="max-w-[600px] mx-auto w-full flex items-center gap-2 h-11 px-2.5">
          {/* Tabs — pill style like Home */}
          <div className="inline-flex items-center h-11 px-1 rounded-full bg-white/[0.06] backdrop-blur-xl border border-white/[0.09]">
            {(['for-you', 'following'] as const).map((t2) => {
              const isActive = tab === t2
              return (
                <button
                  key={t2}
                  onClick={() => setTab(t2)}
                  className={`relative shrink-0 px-3.5 py-2 rounded-full whitespace-nowrap text-[13px] font-semibold transition-colors tap-highlight-transparent ${
                    isActive ? 'text-text' : 'text-text-hint hover:text-text'
                  }`}
                >
                  {isActive && (
                    <motion.div
                      layoutId="feedTabPill"
                      className="absolute inset-0 bg-white/[0.08] rounded-full border border-white/[0.09]"
                      transition={{ type: 'spring', stiffness: 350, damping: 28 }}
                    />
                  )}
                  <span className="relative z-10">{t(`feed.tab.${t2}`)}</span>
                </button>
              )
            })}
          </div>

          {/* Search button */}
          <button
            onClick={() => setSearchOpen(true)}
            className="ml-auto w-10 h-10 flex items-center justify-center rounded-full bg-white/[0.06] backdrop-blur-xl border border-white/[0.09] tap-highlight-transparent hover:bg-white/[0.1] transition-colors"
          >
            <Search size={18} className="text-text-hint/60" />
          </button>
        </div>
      </div>

      {/* ── Pull-to-refresh indicator ── */}
      <div
        className="flex items-center justify-center overflow-hidden transition-[height] duration-200"
        style={{ height: pullDistance > 0 || isRefreshing ? `${Math.max(pullDistance, isRefreshing ? 40 : 0)}px` : '0px' }}
      >
        <div
          className={isRefreshing ? 'animate-spin' : 'transition-transform'}
          style={!isRefreshing ? { transform: `rotate(${Math.min(pullDistance / PULL_THRESHOLD * 360, 360)}deg)` } : undefined}
        >
          <Loader2 size={22} className="text-primary" />
        </div>
      </div>

      {/* ── Feed content ── */}
      <div className="max-w-[600px] mx-auto pb-28">
        {isLoading ? (
          <FeedSkeleton />
        ) : allPosts.length === 0 ? (
          <div className="px-4 pt-12">
            <EmptyState
              icon={null}
              title={t(`feed.empty.${tab}`)}
              description={t(`feed.empty.${tab}Desc`)}
            />
          </div>
        ) : (
          allPosts.map((item) => (
            <PostCard
              key={item.post_id}
              item={item}
              followState={
                item.author_id === userId ? 'following'
                  : followingSet.has(item.author_id) ? 'following'
                  : pendingSet.has(item.author_id) ? 'pending'
                  : 'none'
              }
              onCommentPress={handleCommentPress}
              onFollowPress={handleFollowPress}
              onRepostPress={handleRepostPress}
            />
          ))
        )}

        {/* Loading more indicator */}
        {activeFeed.isFetchingNextPage && (
          <div className="flex justify-center py-6">
            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>

      {/* FAB — create post */}
      <div className="fixed bottom-[calc(var(--tg-safe-area-inset-bottom,0px)+80px)] right-4 z-40">
        <button
          onClick={() => setComposerOpen(true)}
          className="w-14 h-14 bg-primary text-white rounded-full flex items-center justify-center shadow-lg shadow-primary/25 hover:shadow-primary/40 hover:scale-105 active:scale-95 transition-all tap-highlight-transparent"
        >
          <Plus size={24} strokeWidth={2.5} />
        </button>
      </div>

      {/* User search overlay */}
      <UserSearch
        userId={userId}
        isOpen={searchOpen}
        onClose={() => setSearchOpen(false)}
      />

      {/* Post composer */}
      <PostComposer
        isOpen={composerOpen}
        onClose={() => setComposerOpen(false)}
        onPostCreated={() => {
          setTab('following')
          scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
        }}
      />

      {/* Repost sheet */}
      <RepostSheet
        post={repostTarget}
        onClose={() => setRepostTarget(null)}
        onReposted={() => {
          setTab('following')
          scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
        }}
      />

      {/* Post detail / comments */}
      {detailPost && (
        <PostDetailSheet
          postId={detailPost.id}
          postType={detailPost.type}
          onClose={() => setDetailPost(null)}
        />
      )}
    </div>
  )
}

// ── Skeleton ──

function FeedSkeleton(): React.ReactElement {
  return (
    <div>
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="px-4 py-4 border-b border-white/[0.06]">
          <div className="flex items-center gap-3 mb-3">
            <Skeleton className="w-10 h-10 rounded-full" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-3.5 w-28" />
              <Skeleton className="h-2.5 w-16" />
            </div>
          </div>
          <Skeleton className="h-4 w-full mb-1.5" />
          <Skeleton className="h-4 w-3/4 mb-3" />
          <Skeleton className="h-[200px] rounded-xl" />
        </div>
      ))}
    </div>
  )
}
